import React, { useState } from "react";
import {
  TextField,
  FormControl,
  InputLabel,
  Select,
  OutlinedInput,
  MenuItem,
  IconButton,
  InputAdornment,
  Checkbox
} from "@mui/material";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import SearchIcon from '@mui/icons-material/Search';
import DeleteIcon from '@mui/icons-material/Delete';
import ClearIcon from '@mui/icons-material/Clear';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import BadgeIcon from '@mui/icons-material/Badge';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import EventIcon from '@mui/icons-material/Event';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import StoreIcon from '@mui/icons-material/Store';
import FlagIcon from '@mui/icons-material/Flag';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import AssignmentIcon from '@mui/icons-material/Assignment';
import ArticleIcon from '@mui/icons-material/Article';
import PhoneIcon from '@mui/icons-material/Phone';
import HomeIcon from '@mui/icons-material/Home';
import WorkIcon from '@mui/icons-material/Work';
import PersonAddAlt1Icon from '@mui/icons-material/PersonAddAlt1';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import TwoWheelerIcon from '@mui/icons-material/TwoWheeler';
import DrawIcon from '@mui/icons-material/Draw';
import { PiMicrosoftExcelLogoBold } from "react-icons/pi";
import {
  estadosOpciones,
  estadoMap,
  estadoDocumentalMap,
  estadoTelefonicaMap,
  DomicilioLaboralMap,
  estadoRespaldosPendientesMap,
} from "./ListadoSolicitud.constants";

// Grid base reutilizada por el bloque siempre-visible y el panel avanzado.
// auto-fit con un mínimo de 200px evita tanto columnas vacías estiradas (una
// fila con menos campos que el máximo) como el texto cortado que se veía
// antes con columnas más angostas.
const GRID_FILTROS = "grid grid-cols-1 sm:grid-cols-[repeat(auto-fit,minmax(200px,1fr))] gap-3";

// sx compartido por los <Select>: el valor cerrado también trunca con
// ellipsis (antes se recortaba de forma abrupta contra el borde del campo).
const SELECT_SX = {
  "& .MuiSelect-select": {
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
};

// Select con ícono de referencia al inicio — mismo patrón repetido por cada
// filtro (Bodega, Vendedor, Analista, etc.), centralizado acá para no
// duplicar el boilerplate de OutlinedInput + InputAdornment 11 veces.
function SelectConIcono({ label, value, onChange, Icon, disabled, minWidth, children }) {
  return (
    <FormControl size="small" fullWidth={!minWidth} disabled={disabled} sx={minWidth ? { minWidth } : undefined}>
      <InputLabel>{label}</InputLabel>
      <Select
        value={value}
        onChange={onChange}
        sx={SELECT_SX}
        input={
          <OutlinedInput
            label={label}
            startAdornment={
              <InputAdornment position="start">
                <Icon fontSize="small" className="text-slate-400" />
              </InputAdornment>
            }
          />
        }
      >
        {children}
      </Select>
    </FormControl>
  );
}

// Botón "x" para limpiar un campo de texto individual sin afectar los demás filtros.
function BotonLimpiarCampo({ visible, onClear, label }) {
  if (!visible) return null;
  return (
    <IconButton size="small" onClick={onClear} aria-label={label} edge="end">
      <ClearIcon fontSize="small" />
    </IconButton>
  );
}

// Chip de filtro activo — mismo lenguaje visual (rounded-lg, border-slate-200,
// shadow-sm) que el resto del panel, con botón de cierre individual.
function ChipFiltro({ label, onRemove }) {
  return (
    <span className="inline-flex items-center gap-1.5 bg-blue-50 text-blue-700 border border-blue-200 rounded-lg pl-3 pr-1.5 py-1 text-xs font-medium shadow-sm">
      <span className="max-w-[220px] truncate" title={label}>{label}</span>
      <button
        type="button"
        onClick={onRemove}
        aria-label={`Quitar filtro: ${label}`}
        className="flex items-center justify-center w-4 h-4 rounded-full text-blue-500 hover:bg-blue-200 hover:text-blue-800 transition-colors cursor-pointer"
      >
        <ClearIcon sx={{ fontSize: 12 }} />
      </button>
    </span>
  );
}

export function FiltrosListado({
  fechaInicio, setFechaInicio,
  fechaFin, setFechaFin,
  userData,
  selectedBodega, setSelectedBodega, bodegas,
  selectedVendedor, setSelectedVendedor, vendedores,
  analistaSelected, setAnalistaSelected, analistas,
  operadorSelected, setOperadorSelected, operadores,
  estado, setEstado,
  solicitud, setSolicitud,
  documental, setDocumental,
  telefonica, setTelefonica,
  domicilio, setDomicilio,
  laboral, setLaboral,
  cedula, setCedula,
  nombre, setNombre,
  numeroSolicitud, setNumeroSolicitud,
  motivoContinuidad, setMotivoContinuidad,
  tieneRespaldosPendientes, setTieneRespaldosPendientes,
  estadoRespaldosPendientes, setEstadoRespaldosPendientes,
  tipoFirmaElectronica, setTipoFirmaElectronica, tiposFirmaElectronica,
  handleConsultar, loadingConsulta, limpiarFiltros,
  puedeCrearSolicitud, handleSolictud,
  puedeCrearSolicitudNueva, handleNuevaSolicitud,
  imprimirInforme, setOpenModalExcel,
  crearYupana, handleSolictudYupana
}) {
  // Los filtros "avanzados" (menos usados día a día) empiezan colapsados,
  // salvo que ya venga alguno aplicado (ej. al volver de otra pantalla) —
  // así el usuario no se pregunta por qué el listado se ve filtrado sin
  // ver por qué.
  const hayAvanzadosActivos =
    analistaSelected !== "todos" ||
    operadorSelected !== "todos" ||
    solicitud !== "Todos" ||
    documental !== "Todos" ||
    telefonica !== "Todos" ||
    domicilio !== "Todos" ||
    laboral !== "Todos" ||
    !!motivoContinuidad ||
    !!tieneRespaldosPendientes ||
    tipoFirmaElectronica !== "todos";
  const [mostrarAvanzados, setMostrarAvanzados] = useState(hayAvanzadosActivos);

  const nombrePorId = (lista, idKey, nombreKey, id) =>
    lista.find((item) => String(item[idKey]) === String(id))?.[nombreKey]?.trim();

  // Un chip por filtro realmente aplicado (distinto de su valor "sin filtrar").
  // Cada uno sabe limpiarse a sí mismo reutilizando el mismo setter + clave de
  // sessionStorage que ya usa el campo correspondiente — no hay lógica nueva,
  // solo una vista de resumen sobre lo que ya existía.
  const filtrosActivos = [];
  if (selectedBodega !== "todos") {
    filtrosActivos.push({
      id: "bodega",
      label: `Bodega: ${nombrePorId(bodegas, "b_Bodega", "b_Nombre", selectedBodega) || selectedBodega}`,
      onRemove: () => { setSelectedBodega("todos"); sessionStorage.removeItem("filtroBodega"); },
    });
  }
  if (selectedVendedor !== "todos") {
    filtrosActivos.push({
      id: "vendedor",
      label: `Vendedor: ${nombrePorId(vendedores, "idPersonal", "Nombre", selectedVendedor) || selectedVendedor}`,
      onRemove: () => { setSelectedVendedor("todos"); sessionStorage.removeItem("filtroVendedor"); },
    });
  }
  if (analistaSelected !== "todos") {
    filtrosActivos.push({
      id: "analista",
      label: `Analista: ${nombrePorId(analistas, "idUsuario", "Nombre", analistaSelected) || analistaSelected}`,
      onRemove: () => { setAnalistaSelected("todos"); sessionStorage.removeItem("filtroAnalista"); },
    });
  }
  if (operadorSelected !== "todos") {
    filtrosActivos.push({
      id: "operador",
      label: `Operador: ${nombrePorId(operadores, "idUsuario", "Nombre", operadorSelected) || operadorSelected}`,
      onRemove: () => { setOperadorSelected("todos"); sessionStorage.removeItem("filtroOperador"); },
    });
  }
  if (estado !== "todos") {
    filtrosActivos.push({
      id: "estado",
      label: `Estado: ${estadosOpciones.find((o) => String(o.value) === String(estado))?.label || estado}`,
      onRemove: () => { setEstado("todos"); sessionStorage.removeItem("filtroEstado"); },
    });
  }
  if (solicitud !== "Todos") {
    filtrosActivos.push({
      id: "solicitud",
      label: `Solicitud: ${estadoMap[solicitud] || solicitud}`,
      onRemove: () => { setSolicitud("Todos"); sessionStorage.removeItem("filtroSolicitud"); },
    });
  }
  if (documental !== "Todos") {
    filtrosActivos.push({
      id: "documental",
      label: `Documental: ${estadoDocumentalMap[documental] || documental}`,
      onRemove: () => { setDocumental("Todos"); sessionStorage.removeItem("filtroDocumental"); },
    });
  }
  if (telefonica !== "Todos") {
    filtrosActivos.push({
      id: "telefonica",
      label: `Telefónica: ${estadoTelefonicaMap[telefonica] || telefonica}`,
      onRemove: () => { setTelefonica("Todos"); sessionStorage.removeItem("filtroTelefonica"); },
    });
  }
  if (domicilio !== "Todos") {
    filtrosActivos.push({
      id: "domicilio",
      label: `Domicilio: ${DomicilioLaboralMap[domicilio] || domicilio}`,
      onRemove: () => { setDomicilio("Todos"); sessionStorage.removeItem("filtroDomicilio"); },
    });
  }
  if (laboral !== "Todos") {
    filtrosActivos.push({
      id: "laboral",
      label: `Laboral: ${DomicilioLaboralMap[laboral] || laboral}`,
      onRemove: () => { setLaboral("Todos"); sessionStorage.removeItem("filtroLaboral"); },
    });
  }
  if (cedula) {
    filtrosActivos.push({
      id: "cedula",
      label: `Cédula: ${cedula}`,
      onRemove: () => { setCedula(""); sessionStorage.removeItem("filtroCedula"); },
    });
  }
  if (nombre) {
    filtrosActivos.push({
      id: "nombre",
      label: `Nombre: ${nombre}`,
      onRemove: () => { setNombre(""); sessionStorage.removeItem("filtroNombre"); },
    });
  }
  if (numeroSolicitud) {
    filtrosActivos.push({
      id: "numeroSolicitud",
      label: `N° Solicitud: ${numeroSolicitud}`,
      onRemove: () => { setNumeroSolicitud(""); sessionStorage.removeItem("filtroNumSolicitud"); },
    });
  }
  if (motivoContinuidad) {
    filtrosActivos.push({
      id: "motivoContinuidad",
      label: "Pendiente por Gestionar",
      onRemove: () => { setMotivoContinuidad(false); sessionStorage.removeItem("filtroMotivoContinuidad"); },
    });
  }
  if (tipoFirmaElectronica !== "todos") {
    filtrosActivos.push({
      id: "tipoFirmaElectronica",
      label: `Firma Electrónica: ${nombrePorId(tiposFirmaElectronica, "TipoFirmaElectronica", "Nombre", tipoFirmaElectronica) || tipoFirmaElectronica}`,
      onRemove: () => { setTipoFirmaElectronica("todos"); sessionStorage.removeItem("filtroTipoFirmaElectronica"); },
    });
  }
  if (tieneRespaldosPendientes) {
    const estadoTexto = estadoRespaldosPendientes !== "Todos" ? estadoRespaldosPendientesMap[estadoRespaldosPendientes] : null;
    filtrosActivos.push({
      id: "respaldosPendientes",
      label: `Con Respaldos Pendientes${estadoTexto ? `: ${estadoTexto}` : ""}`,
      onRemove: () => {
        setTieneRespaldosPendientes(false);
        setEstadoRespaldosPendientes("Todos");
        sessionStorage.removeItem("filtroTieneRespaldosPendientes");
        sessionStorage.removeItem("filtroEstadoRespaldosPendientes");
      },
    });
  }

  const hayAccionesSecundarias =
    (puedeCrearSolicitud && puedeCrearSolicitud()) ||
    (puedeCrearSolicitudNueva && puedeCrearSolicitudNueva()) ||
    (imprimirInforme && imprimirInforme()) ||
    (crearYupana && crearYupana());

  return (
    <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-4 md:p-5 space-y-4">
      <div className="flex items-center gap-2">
        <FilterAltIcon className="text-slate-400" fontSize="small" />
        <h2 className="text-sm font-bold text-slate-800">Filtros de búsqueda</h2>
      </div>

      {/* Filtros de uso frecuente — siempre visibles */}
      <div className={GRID_FILTROS}>
        <TextField
          label="Fecha Desde"
          type="date"
          variant="outlined"
          value={fechaInicio}
          onChange={(e) => { const fechaIniFiltro = e.target.value; setFechaInicio(fechaIniFiltro); sessionStorage.setItem('filtroIniFecha', fechaIniFiltro); }}
          fullWidth
          size="small"
          slotProps={{
            inputLabel: { shrink: true },
            input: {
              min: userData?.FechaIngreso
                ? new Date(userData.FechaIngreso).toISOString().split('T')[0]
                : undefined,
              startAdornment: (
                <InputAdornment position="start">
                  <EventIcon fontSize="small" className="text-slate-400" />
                </InputAdornment>
              ),
            },
          }}
        />
        <TextField
          label="Fecha Hasta"
          type="date"
          variant="outlined"
          value={fechaFin}
          onChange={(e) => { const fechaFinFiltro = e.target.value; setFechaFin(fechaFinFiltro); sessionStorage.setItem('filtroFinFecha', fechaFinFiltro); }}
          fullWidth
          size="small"
          InputLabelProps={{ shrink: true }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <EventAvailableIcon fontSize="small" className="text-slate-400" />
              </InputAdornment>
            ),
          }}
        />
        <SelectConIcono
          label="Bodega"
          value={selectedBodega}
          onChange={(e) => { const bodegaFiltro = e.target.value; setSelectedBodega(bodegaFiltro); sessionStorage.setItem('filtroBodega', bodegaFiltro); }}
          Icon={StoreIcon}
          disabled={userData?.idGrupo === 23}
        >
          <MenuItem value="todos">Todos</MenuItem>
          {bodegas.map((bodega) => (
            <MenuItem key={bodega.b_Bodega} value={bodega.b_Bodega} title={bodega.b_Nombre}>
              {bodega.b_Nombre}
            </MenuItem>
          ))}
        </SelectConIcono>
        <SelectConIcono
          label="Vendedor"
          value={selectedVendedor}
          onChange={(e) => { const vendedorFiltro = e.target.value; setSelectedVendedor(vendedorFiltro); sessionStorage.setItem('filtroVendedor', vendedorFiltro); }}
          Icon={PersonOutlineIcon}
          disabled={userData?.idGrupo === 23}
        >
          {userData?.idGrupo !== 23 && (<MenuItem value="todos">Todos</MenuItem>)}
          {(userData?.idGrupo === 23 ? vendedores.filter(
            (vendedor) => vendedor?.Codigo === userData?.Nombre)
            : vendedores
          ).map((vendedor) => {
            const nombreVendedor = `${vendedor.Nombre || ""}`.trim() || "No disponible";
            return (
              <MenuItem key={vendedor.idPersonal} value={vendedor.idPersonal} title={nombreVendedor}>
                {nombreVendedor}
              </MenuItem>
            );
          })}
        </SelectConIcono>
        <SelectConIcono
          label="Estado"
          value={estado}
          onChange={(e) => { const estadoFiltro = e.target.value; setEstado(estadoFiltro); sessionStorage.setItem('filtroEstado', estadoFiltro); }}
          Icon={FlagIcon}
        >
          {estadosOpciones.map((estadoOpcion) => (
            <MenuItem key={estadoOpcion.value} value={estadoOpcion.value} title={estadoOpcion.label}>
              {estadoOpcion.label}
            </MenuItem>
          ))}
        </SelectConIcono>
        <TextField
          label="Cédula"
          variant="outlined"
          value={cedula}
          onChange={(e) => { const cedulaFiltro = e.target.value; setCedula(cedulaFiltro); sessionStorage.setItem('filtroCedula', cedulaFiltro); }}
          fullWidth
          size="small"
          InputLabelProps={{ shrink: true }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <BadgeIcon fontSize="small" className="text-slate-400" />
              </InputAdornment>
            ),
            endAdornment: (
              <BotonLimpiarCampo
                visible={!!cedula}
                label="Limpiar cédula"
                onClear={() => { setCedula(''); sessionStorage.removeItem('filtroCedula'); }}
              />
            ),
          }}
        />
        <TextField
          label="Nombre del cliente"
          variant="outlined"
          value={nombre}
          onChange={(e) => { const nombreFiltro = e.target.value; setNombre(nombreFiltro); sessionStorage.setItem('filtroNombre', nombreFiltro); }}
          fullWidth
          size="small"
          InputLabelProps={{ shrink: true }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <PersonSearchIcon fontSize="small" className="text-slate-400" />
              </InputAdornment>
            ),
            endAdornment: (
              <BotonLimpiarCampo
                visible={!!nombre}
                label="Limpiar nombre"
                onClear={() => { setNombre(''); sessionStorage.removeItem('filtroNombre'); }}
              />
            ),
          }}
        />
        <TextField
          label="N° de solicitud"
          variant="outlined"
          value={numeroSolicitud}
          onChange={(e) => { const numSoliFiltro = e.target.value; setNumeroSolicitud(numSoliFiltro); sessionStorage.setItem('filtroNumSolicitud', numSoliFiltro); }}
          fullWidth
          size="small"
          InputLabelProps={{ shrink: true }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <DescriptionOutlinedIcon fontSize="small" className="text-slate-400" />
              </InputAdornment>
            ),
            endAdornment: (
              <BotonLimpiarCampo
                visible={!!numeroSolicitud}
                label="Limpiar número de solicitud"
                onClear={() => { setNumeroSolicitud(''); sessionStorage.removeItem('filtroNumSolicitud'); }}
              />
            ),
          }}
        />
      </div>

      {/* Panel colapsable: filtros de menor uso (asignación fina + estados
          de verificación por área + filtros especiales) — reduce la
          saturación visual del bloque principal sin quitarles alcance. */}
      <div className="border-t border-slate-100 pt-3">
        <button
          type="button"
          onClick={() => setMostrarAvanzados((v) => !v)}
          className="flex items-center gap-1.5 text-sm font-semibold text-slate-600 hover:text-slate-800 cursor-pointer transition-colors"
          aria-expanded={mostrarAvanzados}
        >
          <ExpandMoreIcon
            fontSize="small"
            className={`transition-transform duration-200 ${mostrarAvanzados ? "rotate-180" : ""}`}
          />
          <span>{mostrarAvanzados ? "Ocultar filtros avanzados" : "Mostrar filtros avanzados"}</span>
        </button>

        {mostrarAvanzados && (
          <div className="mt-3 space-y-3">
            <div className={GRID_FILTROS}>
              <SelectConIcono
                label="Analista"
                value={analistaSelected}
                onChange={(e) => { const analistaFiltro = e.target.value; setAnalistaSelected(analistaFiltro); sessionStorage.setItem('filtroAnalista', analistaFiltro); }}
                Icon={AssignmentIndIcon}
              >
                <MenuItem value="todos">Todos</MenuItem>
                {analistas.map((vendedor) => {
                  const nombreAnalista = vendedor.Nombre?.trim() || "No disponible";
                  return (
                    <MenuItem key={vendedor.idUsuario} value={vendedor.idUsuario} title={nombreAnalista}>
                      {nombreAnalista}
                    </MenuItem>
                  );
                })}
              </SelectConIcono>
              <SelectConIcono
                label="Operador"
                value={operadorSelected}
                onChange={(e) => { const operadorFiltro = e.target.value; setOperadorSelected(operadorFiltro); sessionStorage.setItem('filtroOperador', operadorFiltro); }}
                Icon={SupportAgentIcon}
              >
                <MenuItem value="todos">Todos</MenuItem>
                {operadores.map((operador) => {
                  const nombreOperador = operador.Nombre?.trim() || "No disponible";
                  return (
                    <MenuItem key={operador.idUsuario} value={operador.idUsuario} title={nombreOperador}>
                      {nombreOperador}
                    </MenuItem>
                  );
                })}
              </SelectConIcono>
              <SelectConIcono
                label="Firma Electrónica"
                value={tipoFirmaElectronica}
                onChange={(e) => { const tipoFirmaFiltro = e.target.value; setTipoFirmaElectronica(tipoFirmaFiltro); sessionStorage.setItem('filtroTipoFirmaElectronica', tipoFirmaFiltro); }}
                Icon={DrawIcon}
              >
                <MenuItem value="todos">Todos</MenuItem>
                {tiposFirmaElectronica.map((tipo) => {
                  const nombreTipo = tipo.Nombre?.trim() || "No disponible";
                  return (
                    <MenuItem key={tipo.idTipoFirmaElectronica} value={tipo.TipoFirmaElectronica} title={nombreTipo}>
                      {nombreTipo}
                    </MenuItem>
                  );
                })}
              </SelectConIcono>
              <SelectConIcono
                label="Solicitud"
                value={solicitud}
                onChange={(e) => { const solicitudFiltro = e.target.value; setSolicitud(solicitudFiltro); sessionStorage.setItem('filtroSolicitud', solicitudFiltro); }}
                Icon={AssignmentIcon}
              >
                <MenuItem value="Todos">Todos</MenuItem>
                {Object.entries(estadoMap).map(([key, label]) => (
                  <MenuItem key={key} value={key} title={label}>
                    {label}
                  </MenuItem>
                ))}
              </SelectConIcono>
              <SelectConIcono
                label="Documental"
                value={documental}
                onChange={(e) => { const documentalFiltro = e.target.value; setDocumental(documentalFiltro); sessionStorage.setItem('filtroDocumental', documentalFiltro); }}
                Icon={ArticleIcon}
              >
                <MenuItem value="Todos">Todos</MenuItem>
                {Object.entries(estadoDocumentalMap).map(([key, label]) => (
                  <MenuItem key={key} value={key} title={label}>
                    {label}
                  </MenuItem>
                ))}
              </SelectConIcono>
              <SelectConIcono
                label="Telefónica"
                value={telefonica}
                onChange={(e) => { const telefonicaFiltro = e.target.value; setTelefonica(telefonicaFiltro); sessionStorage.setItem('filtroTelefonica', telefonicaFiltro); }}
                Icon={PhoneIcon}
              >
                <MenuItem value="Todos">Todos</MenuItem>
                {Object.entries(estadoTelefonicaMap).map(([key, label]) => (
                  <MenuItem key={key} value={key} title={label}>
                    {label}
                  </MenuItem>
                ))}
              </SelectConIcono>
              <SelectConIcono
                label="Domicilio"
                value={domicilio}
                onChange={(e) => { const domicilioFiltro = e.target.value; setDomicilio(domicilioFiltro); sessionStorage.setItem('filtroDomicilio', domicilioFiltro); }}
                Icon={HomeIcon}
              >
                <MenuItem value="Todos">Todos</MenuItem>
                {Object.entries(DomicilioLaboralMap).map(([key, label]) => (
                  <MenuItem key={key} value={key} title={label}>
                    {label}
                  </MenuItem>
                ))}
              </SelectConIcono>
              <SelectConIcono
                label="Laboral"
                value={laboral}
                onChange={(e) => { const LaboralFiltro = e.target.value; setLaboral(LaboralFiltro); sessionStorage.setItem('filtroLaboral', LaboralFiltro); }}
                Icon={WorkIcon}
              >
                <MenuItem value="Todos">Todos</MenuItem>
                {Object.entries(DomicilioLaboralMap).map(([key, label]) => (
                  <MenuItem key={key} value={key} title={label}>
                    {label}
                  </MenuItem>
                ))}
              </SelectConIcono>
            </div>

            <div className="flex flex-wrap items-center gap-2.5">
              <label
                className={`flex items-center gap-2 cursor-pointer text-sm font-medium rounded-lg px-3 py-1.5 border shadow-sm transition-colors duration-200 ${motivoContinuidad
                    ? "bg-indigo-50 border-indigo-200 text-indigo-800"
                    : "bg-slate-50 border-slate-200 text-slate-600 hover:bg-slate-100"
                  }`}
              >
                <Checkbox
                  checked={motivoContinuidad}
                  onChange={(e) => {
                    const value = e.target.checked;
                    setMotivoContinuidad(value);
                    sessionStorage.setItem('filtroMotivoContinuidad', value);
                  }}
                  icon={<AssignmentTurnedInIcon fontSize="small" className="text-slate-400" />}
                  checkedIcon={<AssignmentTurnedInIcon fontSize="small" className="text-indigo-600" />}
                  size="small"
                  sx={{ p: 0.5 }}
                />
                <span>Pendiente por Gestionar</span>
              </label>

              <label
                className={`flex items-center gap-2 cursor-pointer text-sm font-medium rounded-lg px-3 py-1.5 border shadow-sm transition-colors duration-200 ${tieneRespaldosPendientes
                    ? "bg-amber-50 border-amber-200 text-amber-800"
                    : "bg-slate-50 border-slate-200 text-slate-600 hover:bg-slate-100"
                  }`}
              >
                <Checkbox
                  checked={tieneRespaldosPendientes}
                  onChange={(e) => {
                    const value = e.target.checked;
                    setTieneRespaldosPendientes(value);
                    sessionStorage.setItem('filtroTieneRespaldosPendientes', value);
                    if (!value) {
                      setEstadoRespaldosPendientes("Todos");
                      sessionStorage.removeItem('filtroEstadoRespaldosPendientes');
                    }
                  }}
                  icon={<AttachFileIcon fontSize="small" className="text-slate-400" />}
                  checkedIcon={<AttachFileIcon fontSize="small" className="text-amber-600" />}
                  size="small"
                  sx={{ p: 0.5 }}
                />
                <span>Con Respaldos Pendientes</span>
              </label>

              {tieneRespaldosPendientes && (
                <SelectConIcono
                  label="Estado de Respaldos"
                  value={estadoRespaldosPendientes}
                  onChange={(e) => {
                    const valor = e.target.value;
                    setEstadoRespaldosPendientes(valor);
                    sessionStorage.setItem('filtroEstadoRespaldosPendientes', valor);
                  }}
                  Icon={AttachFileIcon}
                  minWidth={200}
                >
                  <MenuItem value="Todos">Todos</MenuItem>
                  {Object.entries(estadoRespaldosPendientesMap).map(([key, label]) => (
                    <MenuItem key={key} value={key} title={label}>
                      {label}
                    </MenuItem>
                  ))}
                </SelectConIcono>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Tags de filtros activos */}
      {filtrosActivos.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 border-t border-slate-100 pt-3">
          <span className="text-xs font-semibold text-slate-500 mr-1">Filtros activos:</span>
          {filtrosActivos.map((filtro) => (
            <ChipFiltro key={filtro.id} label={filtro.label} onRemove={filtro.onRemove} />
          ))}
          <button
            type="button"
            onClick={limpiarFiltros}
            className="text-xs font-semibold text-slate-500 hover:text-red-600 underline decoration-dotted underline-offset-2 cursor-pointer transition-colors ml-1"
          >
            Limpiar todo
          </button>
        </div>
      )}

      {/* Acciones */}
      <div className="flex flex-wrap items-center justify-center gap-3 border-t border-slate-100 pt-3">
        <button
          className="group relative flex items-center justify-center gap-2.5 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg shadow-sm hover:shadow-md transition-all duration-200 disabled:opacity-70 disabled:cursor-not-allowed min-w-[150px] focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 focus-visible:ring-offset-2"
          onClick={handleConsultar}
          disabled={loadingConsulta}
        >
          {loadingConsulta ? (
            <>
              <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <span>Consultando...</span>
            </>
          ) : (
            <>
              <SearchIcon fontSize="small" />
              <span>Aplicar filtros</span>
            </>
          )}
        </button>
        <button
          className="group flex items-center gap-2 px-5 py-3 bg-white border border-slate-300 hover:bg-slate-50 text-slate-700 font-semibold rounded-lg shadow-sm hover:shadow-md transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 focus-visible:ring-offset-2"
          onClick={limpiarFiltros}
        >
          <DeleteIcon fontSize="small" className="text-slate-500" />
          <span>Limpiar{filtrosActivos.length > 0 ? ` (${filtrosActivos.length})` : ""}</span>
        </button>

        {hayAccionesSecundarias && (
          <span className="hidden sm:block w-px h-8 bg-slate-200" aria-hidden="true" />
        )}

        {puedeCrearSolicitud && puedeCrearSolicitud() && (
          <button
            title="Nueva Solicitud"
            className="flex items-center gap-2 px-4 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold rounded-lg shadow-sm hover:shadow-md transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-400 focus-visible:ring-offset-2"
            onClick={handleSolictud}
          >
            <PersonAddAlt1Icon fontSize="small" />
            <span>Nueva Solicitud</span>
          </button>
        )}

        {/* Mismo texto base que el botón anterior pero con ícono y sufijo "V2"
            distintos: antes eran indistinguibles salvo por el color, lo que
            no es una señal accesible ni confiable para diferenciar dos
            acciones de creación distintas. */}
        {puedeCrearSolicitudNueva && puedeCrearSolicitudNueva() && (
          <button
            title="Nueva Solicitud V2"
            className="flex items-center gap-2 px-4 py-2.5 bg-fuchsia-600 hover:bg-fuchsia-700 text-white text-sm font-semibold rounded-lg shadow-sm hover:shadow-md transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-fuchsia-400 focus-visible:ring-offset-2"
            onClick={handleNuevaSolicitud}
          >
            <GroupAddIcon fontSize="small" />
            <span>Nueva Solicitud V2</span>
          </button>
        )}

        {imprimirInforme && imprimirInforme() && (
          <button
            className="flex items-center gap-2 px-4 py-2.5 bg-green-600 hover:bg-green-700 text-white text-sm font-semibold rounded-lg shadow-sm hover:shadow-md transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-green-400 focus-visible:ring-offset-2"
            onClick={() => setOpenModalExcel(true)}
          >
            <PiMicrosoftExcelLogoBold size={18} />
            <span>Crear Informe</span>
          </button>
        )}

        {crearYupana && crearYupana() && (
          // Botón "Motos" con identidad propia (degradado naranja-marrón, rounded-full,
          // ícono cruiser) — deliberadamente distinto del resto para que la categoría
          // resalte, pero ajustado a la altura/tamaño de sus botones vecinos en la fila
          // (el spec original pedía px-8 py-4 text-lg, que rompía la alineación).
          <button
            title="Nueva Solicitud - Motos"
            aria-label="Nueva Solicitud - Motos"
            onClick={handleSolictudYupana}
            className="flex items-center gap-2 px-4 py-2.5 bg-gradient-to-b from-[#b3592d] to-[#994d26] text-white rounded-full shadow-sm hover:shadow-md border border-[#a3522a] font-sans font-semibold text-sm uppercase tracking-wider hover:from-[#c26131] hover:to-[#a65429] active:shadow-inner active:scale-[0.98] transition-all duration-150 ease-in-out focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-400 focus-visible:ring-offset-2"
          >
            <TwoWheelerIcon className="text-white" fontSize="small" />
            <span>Motos</span>
          </button>
        )}
      </div>
    </div>
  );
}
